/**
 * WidgetGridCanvas — draggable/resizable 12-col preview canvas for
 * dashboard widgets.
 *
 * Uses react-grid-layout. The whole tile is the drag handle; the SE
 * corner is the resize handle. Layout changes bubble via
 * `onLayoutChange`, which the parent merges back into the widget
 * array before the next save.
 *
 * Extracted from `EmbedContentViewerPage.js` in the iter-222 refactor.
 * Consumes the shared `useLiveWidgets` cycle so mounting the canvas
 * on the same page as the WidgetList doesn't fire duplicate refresh
 * requests — both share a single batch behind the visibility-pause.
 *
 * Read-only viewers never see this component (parent only mounts it
 * when `canWrite === true`).
 */
import React from 'react';
import GridLayout from 'react-grid-layout';
import { Move } from 'lucide-react';
import { useLiveWidgets } from '../../lib/useLiveWidgets';
import { LiveRefreshBadge } from './LiveRefreshBadge';
import { LiveDataMiniChart } from './LiveDataMiniChart';
import { WidgetLivePreview } from './WidgetLivePreview';

export function WidgetGridCanvas({ widgets, onLayoutChange, token }) {
  const layout = React.useMemo(() => widgets.map((w) => ({
    i: w.widget_id,
    x: Number.isFinite(w.x) ? w.x : 0,
    y: Number.isFinite(w.y) ? w.y : 0,
    w: Math.max(1, Math.min(12, w.w || 4)),
    h: Math.max(1, w.h || 3),
    minW: 1,
    minH: 1,
    maxW: 12,
  })), [widgets]);
  const { data: liveData, refreshedAt, refreshNow } = useLiveWidgets(
    token, widgets, { intervalSeconds: 60, enabled: !!token },
  );
  const typeTone = (t) => {
    if (t === 'kpi') return 'border-indigo-400/40 bg-indigo-500/10 text-indigo-300';
    if (t === 'bar_chart' || t === 'line_chart') return 'border-emerald-400/40 bg-emerald-500/10 text-emerald-300';
    if (t === 'table') return 'border-amber-400/40 bg-amber-500/10 text-amber-300';
    return 'border-border/40 bg-muted/30 text-muted-foreground';
  };
  return (
    <div className="mt-2 mb-4" data-testid="embed-viewer-canvas">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
          <Move className="w-3 h-3" /> Canvas · 12 col grid · drag to arrange, drag SE corner to resize
        </div>
      </div>
      <div className="rounded-lg border border-border/40 bg-background/30 p-2">
        <GridLayout
          className="layout"
          layout={layout}
          cols={12}
          rowHeight={40}
          width={860}
          margin={[6, 6]}
          containerPadding={[0, 0]}
          onLayoutChange={onLayoutChange}
          compactType={null}
          preventCollision={false}
          isResizable
          isDraggable
          draggableCancel="input,textarea,select,button"
        >
          {widgets.map((w) => {
            const liveEntry = liveData?.[w.widget_id];
            const isLive = !!w.chart_config?.is_live && !!liveEntry;
            return (
              <div
                key={w.widget_id}
                data-testid={`embed-viewer-canvas-tile-${w.widget_id}`}
                className={`rounded border ${typeTone(w.widget_type)} p-2 overflow-hidden cursor-move select-none flex flex-col`}
              >
                <div className="text-[9px] uppercase tracking-wider opacity-80 flex items-center justify-between">
                  <span>{w.widget_type || 'kpi'}</span>
                  <LiveRefreshBadge
                    widget={w}
                    refreshedAt={refreshedAt}
                    onRefresh={refreshNow}
                    error={liveEntry?.error}
                  />
                </div>
                <div className="text-xs font-medium truncate mt-0.5">
                  {w.title || '(untitled)'}
                </div>
                <div className="mt-auto">
                  {isLive
                    ? <LiveDataMiniChart widget={w} liveData={liveEntry} />
                    : <WidgetLivePreview widget={w} token={token} />}
                </div>
              </div>
            );
          })}
        </GridLayout>
      </div>
    </div>
  );
}

export default WidgetGridCanvas;
